import {
  MilitaryTech,
  QueryStats,
  TwoWheeler,
  WhatsApp,
} from "@mui/icons-material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PaymentsIcon from "@mui/icons-material/Payments";
import { AnimatePresence, motion } from "framer-motion";
import {
  BarChart3,
  BriefcaseBusinessIcon,
  ChevronRight,
  CreditCard,
  DownloadCloud,
  Headset,
  DollarSignIcon,
  Edit2Icon,
  FileArchiveIcon,
  Flower,
  Globe,
  Layers,
  LayoutDashboard,
  ListChecks,
  MessageSquareWarningIcon,
  PackageSearch,
  PlusCircle,
  Receipt,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Users,
  X,
} from "lucide-react";
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SuporteBadges } from "../../features/suporte";
import { cn } from "../../lib/utils";
import { screensHabilitadas } from "../../utils/config";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path?: string;
  children?: MenuItem[];
  requiredScreen?: string;
  // Indicador ao lado do nome (ex.: contador da fila). Só é montado quando o
  // item está visível, então não consulta nada para quem não tem a tela.
  badge?: React.ReactElement;
}

const screens = screensHabilitadas;

const menuItems: MenuItem[] = [
  {
    text: "Início",
    icon: <LayoutDashboard className="h-4 w-4" />,
    path: "/",
  },
  {
    text: "OPs e Usuários",
    icon: <Settings className="h-4 w-4" />,
    children: [
      {
        text: "Gerenciar Usuários",
        icon: <Users className="h-4 w-4" />,
        path: "/user-management",
      },
      {
        text: "Gerenciar Funções",
        icon: <ShieldCheck className="h-4 w-4" />,
        path: "/role-management",
      },
      {
        text: "Gerenciar Operações",
        icon: <Settings className="h-4 w-4" />,
        path: "/operations-management",
      },
    ],
  },
  {
    text: "Operações",
    icon: <Layers className="h-4 w-4" />,
    children: [
      {
        text: "Globais",
        icon: <Globe className="h-4 w-4" />,
        children: [
          {
            text: "Checkout Muve",
            icon: <ShoppingCart className="h-4 w-4" />,
            path: "/checkout-resolution/muve",
            requiredScreen: "gestorCheckout",
          },
          {
            text: "Baixa de Créditos",
            icon: <CreditCard className="h-4 w-4" />,
            path: "/baixa-gestor",
            requiredScreen: "gestorBaixa",
          },
        ],
      },
      {
        text: "Indrive",
        icon: <TwoWheeler className="h-4 w-4" />,
        children: [
          {
            text: "Upload Bônus",
            icon: <MilitaryTech className="h-4 w-4" />,
            path: "/indrive/upload-bonus",
            requiredScreen: "indriveBonusUpload",
          },
          {
            text: "Checkout Indrive",
            icon: <ShoppingCart className="h-4 w-4" />,
            path: "/indrive/checkout-resolution",
            requiredScreen: "indriveBonusUpload",
          },
        ],
      },
      {
        text: "Entrego",
        icon: <PaymentsIcon className="h-4 w-4" />,
        children: [
          {
            text: "Entrego Checkout",
            icon: <ShoppingCart className="h-4 w-4" />,
            path: "/entrego/checkout-resolution",
            requiredScreen: "gestorCheckoutEntrego",
          },
          {
            text: "Entrego Baixa",
            icon: <CreditCard className="h-4 w-4" />,
            path: "/entrego/baixa",
            requiredScreen: "gestorBaixa",
          },
          {
            text: "Cadastro de franquias",
            icon: <BriefcaseBusinessIcon className="h-4 w-4" />,
            path: "/entrego/cadastro-franquias",
            requiredScreen: "gestorCheckoutEntrego",
          },
          {
            text: "Créditos AdHoc",
            icon: <DollarSignIcon className="h-4 w-4" />,
            path: "/entrego/credito-adhoc",
            requiredScreen: "gestorCheckoutEntrego",
          },
          {
            text: "Fechamento Entrego",
            icon: <FileArchiveIcon className="h-4 w-4" />,
            path: "/entrego/controle-nfs",
            requiredScreen: "gestorCheckoutEntrego",
          },
        ],
      },
      {
        // O texto do grupo tem que ser único: expandedItems é indexado por
        // item.text, e dois grupos homônimos abrem e fecham juntos.
        // Os itens de Checkout, Baixa e Fechamento entram junto com as rotas
        // deles — item de menu apontando para rota inexistente dá tela em branco.
        text: "iMile",
        icon: <PackageSearch className="h-4 w-4" />,
        children: [
          {
            text: "iMile Checkout",
            icon: <ShoppingCart className="h-4 w-4" />,
            path: "/imile/checkout-resolution",
            requiredScreen: "gestorCheckoutImile",
          },
          {
            text: "iMile Baixa",
            icon: <CreditCard className="h-4 w-4" />,
            path: "/imile/baixa",
            requiredScreen: "gestorCheckoutImile",
          },
          {
            text: "Cadastro de franquias",
            icon: <BriefcaseBusinessIcon className="h-4 w-4" />,
            path: "/imile/cadastro-franquias",
            requiredScreen: "gestorCheckoutImile",
          },
          {
            text: "Fechamento iMile",
            icon: <FileArchiveIcon className="h-4 w-4" />,
            path: "/imile/fechamento",
            requiredScreen: "gestorCheckoutImile",
          },
        ],
      },
    ],
  },
  {
    text: "Viz Ofertas",
    icon: <QueryStats className="h-4 w-4" />,
    children: [
      {
        text: "Rastreamento de Ofertas",
        icon: <PackageSearch className="h-4 w-4" />,
        path: "/entrego/rastreamento-ofertas",
        requiredScreen: "gestorCheckoutEntrego",
      },
      {
        text: "Visualização Antecipação",
        icon: <QueryStats className="h-4 w-4" />,
        path: "/gestor/antecipacao-viz",
        requiredScreen: "gestorBaixa",
      },
      {
        text: "Métricas das Operações",
        icon: <BarChart3 className="h-4 w-4" />,
        path: "/gestor/metricas-ops",
        requiredScreen: "gestorBaixa",
      },
    ],
  },
  {
    text: "Cedentes",
    icon: <FileArchiveIcon className="h-4 w-4" />,
    children: [
      {
        text: "Aprovação de Cadastro",
        icon: <FileArchiveIcon className="h-4 w-4" />,
        path: "/documentos/aprovar",
        requiredScreen: "gestorDocApproval",
      },
      {
        text: "Editar Cadastro",
        icon: <Edit2Icon className="h-4 w-4" />,
        path: "/documentos/editar",
        requiredScreen: "gestorDocApproval",
      },
      {
        text: "Consulta de saldo",
        icon: <AccountBalanceWalletIcon className="h-4 w-4" />,
        path: "/entrego/saldo-consulta",
        requiredScreen: "gestorCheckoutEntrego",
      },
    ],
  },
  {
    text: "Liquidação",
    icon: <Flower className="h-4 w-4" />,
    children: [
      {
        text: "Liquidação Transfeera",
        icon: <DollarSignIcon className="h-4 w-4" />,
        path: "/liquidacao/transfeera",
        requiredScreen: "gestorLiquidacao",
      },
      {
        text: "Liquidação Api",
        icon: <DollarSignIcon className="h-4 w-4" />,
        path: "/liquidacao/payment-request",
        requiredScreen: "gestorLiquidacao",
      },
      {
        text: "Error Pix",
        icon: <MessageSquareWarningIcon className="h-4 w-4" />,
        path: "/liquidacao/error-pix",
        requiredScreen: "gestorErrorPix",
      },
    ],
  },
  {
    text: "Notas Promissórias",
    icon: <Receipt className="h-4 w-4" />,
    children: [
      {
        text: "Controle de NP",
        icon: <ListChecks className="h-4 w-4" />,
        path: "/notas-promissorias",
        requiredScreen: "gestorNotaPromissoria",
      },
      {
        text: "Criar NP",
        icon: <PlusCircle className="h-4 w-4" />,
        path: "/notas-promissorias/criar",
        requiredScreen: "gestorNotaPromissoria",
      },
    ],
  },
  {
    text: "Suporte",
    icon: <Headset className="h-4 w-4" />,
    path: "/suporte",
    requiredScreen: "gestorSuporte",
    badge: <SuporteBadges />,
  },
  {
    text: "Mensagens WhatsApp",
    icon: <WhatsApp className="h-4 w-4" />,
    path: "/whatsapp",
    requiredScreen: "gestorWhatsapp",
  },
  {
    text: "Downloads Retriever",
    icon: <DownloadCloud className="h-4 w-4" />,
    path: "/xupinhador",
    requiredScreen: "gestorXupinhador",
  },
];

/**
 * Textos dos itens que precisam estar abertos para o item de `path` aparecer —
 * a cadeia inteira de ancestrais, não só o pai. Com três níveis de menu, entrar
 * direto em /entrego/baixa tem que abrir "Operações" E "Entrego".
 */
function ancestraisDoPath(itens: MenuItem[], path: string): string[] | null {
  for (const item of itens) {
    if (item.path === path) return [];
    if (item.children) {
      const abaixo = ancestraisDoPath(item.children, path);
      if (abaixo) return [item.text, ...abaixo];
    }
  }
  return null;
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

  useEffect(() => {
    const ancestrais = ancestraisDoPath(menuItems, location.pathname);
    if (!ancestrais?.length) return;

    setExpandedItems((prev) => {
      const faltando = ancestrais.filter((text) => !prev.includes(text));
      return faltando.length > 0 ? [...prev, ...faltando] : prev;
    });
  }, [location.pathname]);

  const handleItemClick = (item: MenuItem) => {
    if (item.path) {
      navigate(item.path);

      if (window.innerWidth < 768) {
        onClose();
      }
    } else if (item.children) {
      const isExpanded = expandedItems.includes(item.text);
      setExpandedItems((prev) =>
        isExpanded
          ? prev.filter((text) => text !== item.text)
          : [...prev, item.text]
      );
    }
  };

  const isItemVisible = (item: MenuItem): boolean => {
    if (!item.requiredScreen && !item.children) {
      return true;
    }

    if (item.children) {
      return item.children.some((child) => isItemVisible(child));
    }

    return Boolean(screens?.includes(String(item?.requiredScreen)));
  };

  const isActive = (path: string): boolean => {
    return location.pathname === path;
  };

  const renderMenuItem = (item: MenuItem, level = 0) => {
    if (!isItemVisible(item)) return null;

    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.text);
    const isChild = level > 0;

    return (
      <motion.div
        key={item.text}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.2 }}
      >
        <Button
          variant={item.path && isActive(item.path) ? "secondary" : "ghost"}
          className={cn(
            "w-full justify-start text-left pl-3 transition-all hover:bg-primary/10",
            // Filhos são menores e mais discretos; pais de grupo ficam em destaque.
            isChild ? "h-9 text-sm text-muted-foreground hover:text-foreground" : "h-10",
            hasChildren && !isChild && "font-medium",
            item.path &&
            isActive(item.path) &&
            "bg-gradient-to-r from-primary/10 to-secondary/10 font-medium text-foreground border-l-2 border-primary"
          )}
          onClick={() => handleItemClick(item)}
        >
          {item.icon}
          <span className="ml-3 flex-1 min-w-0 truncate">{item.text}</span>
          {item.badge}
          {hasChildren && (
            <motion.div
              animate={{ rotate: isExpanded ? 90 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronRight className="h-4 w-4" />
            </motion.div>
          )}
        </Button>

        <AnimatePresence>
          {hasChildren && isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              // Recuo + linha-guia à esquerda: deixa visível quem é filho de quem.
              // Cada nível acumula o recuo do wrapper, sem precisar de classe por nível.
              className="mt-1 space-y-1 overflow-hidden ml-5 pl-2 border-l-2 border-primary/25"
            >
              {item.children!.map((child) => renderMenuItem(child, level + 1))}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <motion.aside
        initial={false}
        animate={{
          x: open ? 0 : "-100%",
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className={cn(
          "fixed left-0 top-0 z-50 h-full w-72 border-r bg-background md:translate-x-0"
        )}
      >
        <div className="flex h-full flex-col">
          <div className="flex h-16 items-center justify-between border-b px-4 bg-gradient-to-r from-primary/5 to-secondary/5">
            <motion.div
              className="flex items-center gap-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg">
                <img
                  src="/favicon.png"
                  alt="Planalto"
                  className="w-6 h-6 object-contain"
                />
              </div>
              <h2 className="text-lg font-semibold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Dashboard Gestor
              </h2>
            </motion.div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="md:hidden hover:bg-primary/10 transition-colors"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <Separator />

          <nav className="flex-1 overflow-auto p-2">
            <div className="space-y-1">
              {menuItems.map((item, index) => {
                const menuItem = renderMenuItem(item);

                return menuItem;
              })}
            </div>
          </nav>
        </div>
      </motion.aside>
    </>
  );
}
